import React, { useContext } from 'react';
import Button from '@mui/material/Button';
import styled from 'styled-components';
import { ThemeContext } from 'styled-components';

const StyledButton = styled(Button)`
  && {
    margin-top: 16px;
    background-color: ${(p) => p.theme.colors.buttonPrimary} !important;
    color: ${(p) => p.theme.colors.buttonPrimaryText} !important;
    -webkit-text-fill-color: ${(p) => p.theme.colors.buttonPrimaryText} !important;
    font-weight: bold;
    font-size: 1.05em;
    opacity: .97 !important;
    transition: box-shadow 180ms ease, background-color 180ms ease, transform 120ms ease;
    min-height: 40px;
    padding: 8px 16px;
    line-height: 1.2;
    white-space: nowrap;
  }
  @media (max-width: 600px) {
    && {
      min-height: 40px;
      padding: 8px 16px;
      font-size: 1.05em;
    }
  }
  &&:hover {
    //background-color: ${(p) => p.theme.colors.brandTertiary} !important;
    font-size: 1.05em;
    font-weight: bold;
    opacity: 1 !important;
    -webkit-text-fill-color: ${(p) => p.theme.colors.buttonPrimaryTextHover} !important;
    box-shadow: 6px 6px 18px rgba(0, 0, 0, 0.22), 3px 3px 8px rgba(0, 0, 0, 0.18);
    transform: translateY(-1px);
  }
  &&:active,
  &&:focus,
  &&.Mui-focusVisible {
    background-color: ${(p) => p.theme.colors.buttonPrimary} !important;
    opacity: 0.7 !important; // Grey out the button
    color: #000000 !important; // Black text
    -webkit-text-fill-color: #000000 !important;
    box-shadow: none !important;
    transform: none !important;
  }
  &&.Mui-disabled {
    background-color: ${(p) => p.theme.colors.buttonPrimary} !important;
    color: #000000 !important; // Black text when disabled/submitting
    -webkit-text-fill-color: #000000 !important;
    opacity: 0.6 !important; // Grey out the button
  }
  &&.Mui-disabled:hover {
    background-color: ${(p) => p.theme.colors.buttonPrimary} !important;
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
    opacity: 0.6 !important;
  }
`;

const ButtonPrimary = ({
    type = "button",
    children,
    onClick,
    autoDisableOnClick = true,
    submittingText = 'submitting...',
    resetAfterMs = 2000,
    disabled: disabledProp,
    ...props
}) => {
    const theme = useContext(ThemeContext);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!isSubmitting) return;
        const t = setTimeout(() => setIsSubmitting(false), resetAfterMs);
        return () => clearTimeout(t);
    }, [isSubmitting, resetAfterMs]);

    const handleClick = (e) => {
        if (disabledProp || isSubmitting) return;
        // Defer disabling for submit buttons to allow native form submit
        if (autoDisableOnClick) {
            if (type === 'submit') {
                setTimeout(() => setIsSubmitting(true), 0);
            } else {
                setIsSubmitting(true);
            }
        }
        if (onClick) onClick(e);
    };

    const isDisabled = Boolean(disabledProp || isSubmitting);

    return (
        <StyledButton
            type={type}
            variant="contained"
            fullWidth
            disableRipple
            onClick={handleClick}
            disabled={isDisabled}
            sx={{
                '&.Mui-disabled': {
                    backgroundColor: `${theme?.colors?.buttonPrimary || '#68ADB7'} !important`,
                    color: '#000000 !important',
                    opacity: '0.6 !important',
                },
                '&.Mui-disabled:hover': {
                    backgroundColor: `${theme?.colors?.buttonPrimary || '#68ADB7'} !important`,
                    color: '#000000 !important',
                    opacity: '0.6 !important',
                },
                ...props.sx,
            }}
            {...props}
        >
            {isSubmitting ? submittingText : children}
        </StyledButton>
    );
};

export default ButtonPrimary;
